import { reduxForm, Field } from 'redux-form/immutable';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { LinearProgress, Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { List } from 'immutable';

import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import { showErrorAction } from 'containers/Notification/actions';
import * as validators from 'utils/validators';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import FormFieldLabel from 'retired/FormFieldLabel';
import { selectUser, selectOrganization, selectUserLocations } from 'entities/user/selectors';
import FormSelectBoxEntitySelector from 'retired/FormSelectBoxEntitySelector';
import PhoneNumber from 'retired/PhoneNumberField/index';
import OrganizationApi from 'entities/organization/api';

import { FORM_IDENTIFIER } from './constants';
import * as Actions from './actions';
import { selectLoading } from './selectors';
import epics from './epics';
import reducer from './reducer';

const key = 'CreateEmployeeFormPage';

const required = validators.required();
const notEmpty = validators.notEmpty();
const isEmail = validators.isEmail();
const isPhone = validators.isPhone();

const CreateEmployeeFormPage = ({
  loading,
  match,
  history,
  user,
  locations,
  organization,
  invalid,
  pristine,
  submitting,
  actions,
  handleSubmit,
}) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    if (user && !user.isAdmin) {
      history.replace('/');
    }
    actions.initAction(match.params.id);
  }, []);
  if (loading) {
    return <LinearProgress />;
  }

  const submit = (data) => {
    actions.saveAction({ organization: organization.id, ...data.toJS() });
  };

  return (
    <Box>
      <Helmet title="Edit Employee" />
      <div>
        <h1>Create New Employee</h1>
        <form onSubmit={handleSubmit(submit)}>
          <fieldset>
            <Grid
              container
              justifyContent="space-between"
              direction="row"
              alignItems="center"
              spacing={3}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={4}>
                <Field
                  id="firstName"
                  name="firstName"
                  component={FormTextField}
                  fullWidth
                  label="First Name *"
                  validate={[required, notEmpty]}
                />
              </Grid>
              <Grid item xs={4}>
                <Field
                  id="lastName"
                  name="lastName"
                  component={FormTextField}
                  fullWidth
                  autoFocus
                  label="Last Name *"
                  validate={[required, notEmpty]}
                />
              </Grid>
              <Grid item xs={4}>
                <Field
                  id="email"
                  name="email"
                  component={FormTextField}
                  fullWidth
                  autoFocus
                  label="Email *"
                  validate={[required, notEmpty, isEmail]}
                />
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="space-between"
              direction="row"
              alignItems="center"
              spacing={3}
              style={{ marginBottom: 20 }}
            >
              <Grid item xs={4}>
                <Field
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  component={PhoneNumber}
                  country={organization?.locationObject.countryCode}
                  fullWidth
                  autoFocus
                  label="Phone Number"
                  validate={isPhone}
                />
              </Grid>

              <Grid item xs={4}>
                <FormFieldLabel>Location *</FormFieldLabel>
                <Field
                  id="location"
                  name="location"
                  component={FormSelectBoxEntitySelector}
                  options={locations}
                  fullWidth
                  autoFocus
                  label="Location *"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                  helperText="The user will be allowed to view deeds from the selected area"
                  validate={[required, notEmpty]}
                  showSearch
                />
              </Grid>

              <Grid item xs={4}>
                <FormFieldLabel>Department</FormFieldLabel>
                <Field
                  id="department"
                  name="department"
                  fullWidth
                  autoFocus
                  component={FormSelectBoxEntitySelector}
                  options={
                    organization?.get('departments').length
                      ? List(organization?.get('departments'))
                      : undefined
                  }
                  fetchOptions={OrganizationApi.fetchDepartments}
                  placeholder="Select departments"
                  label="Department"
                  style={{
                    marginTop: '5px',
                    display: 'block',
                    width: '100%',
                  }}
                  showSearch
                />
              </Grid>
            </Grid>
            <div
              style={{
                display: 'flex',
                justifyContent: 'left',
                alignItems: 'left',
              }}
            >
              <Button
                type="submit"
                style={{ marginRight: '3rem' }}
                variant="contained"
                color="primary"
                disabled={invalid || submitting || pristine}
              >
                Save
              </Button>
              <Button variant="outlined" onClick={(): void => history.push('/volunteers/')}>
                Cancel
              </Button>
            </div>
          </fieldset>
        </form>
      </div>
    </Box>
  );
};
CreateEmployeeFormPage.propTypes = {
  loading: PropTypes.bool,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  user: PropTypes.shape,
  organization: PropTypes.shape,
  locations: PropTypes.shape,
  match: PropTypes.shape,
  history: PropTypes.shape,
  actions: PropTypes.shape,
  handleSubmit: PropTypes.func,
};

const withConnect = connect(
  (state) => ({
    loading: selectLoading(state),
    user: selectUser(state),
    organization: selectOrganization(state),
    locations: selectUserLocations(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
});

export default compose(withRouter, withConnect, withForm, PureRender)(CreateEmployeeFormPage);
